import { axiosInstance, ValidationError } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

type SignDrAgreementContextType = {
  modalInfo: string | undefined;
  fetchSignDrAgreementModalInfo: () => void;
  fetchSignDrAgreement: () => Promise<void>;
};

const SIGN_IMG_HTML_CONTENT =
  '<img src="/assets/signDrAgreement_signature.png">';

const SignDrAgreementContext = createContext<SignDrAgreementContextType>(
  {} as SignDrAgreementContextType,
);

const useSignDrAgreement = (): SignDrAgreementContextType =>
  useContext(SignDrAgreementContext);

const SignDrAgreementProvider = ({ children }: Props): JSX.Element => {
  const [modalInfo, setModalInfo] = useState<string | undefined>();
  const { showError } = useError();

  const fetchSignDrAgreement = useCallback(async (): Promise<any> => {
    try {
      await axiosInstance.post(
        '/client/loans/latest/debt-restructuring/signature',
        {
          method: 'POST',
        },
      );
    } catch (e) {
      showError(e as ValidationError);
      throw e;
    }
  }, [showError]);

  const fetchSignDrAgreementModalInfo = useCallback(async (): Promise<any> => {
    try {
      const { data: modalInfo } = await axiosInstance.get(
        `/client/loans/latest/debt-restructuring/agreement/html`,
        {
          headers: {
            'Content-Type': 'text/html',
            ...(sessionStorage.getItem('token') && {
              Authorization: `Basic ${sessionStorage.getItem('token')}`,
            }),
          },
        },
      );
      // Parse HTML content
      let modalInfoParsed: string = modalInfo.replace(
        SIGN_IMG_HTML_CONTENT,
        '',
      );
      // Move sign image under the table element
      modalInfoParsed = modalInfoParsed.replace(
        '</table>',
        `</table>${SIGN_IMG_HTML_CONTENT}`,
      );

      setModalInfo(modalInfoParsed);
    } catch (e) {
      showError(e as ValidationError);
    }
  }, [showError]);

  const SignDrAgreementContextValue = useMemo(
    () => ({
      modalInfo,
      fetchSignDrAgreementModalInfo,
      fetchSignDrAgreement,
    }),
    [fetchSignDrAgreementModalInfo, fetchSignDrAgreement, modalInfo],
  );

  return (
    <SignDrAgreementContext.Provider value={SignDrAgreementContextValue}>
      {children}
    </SignDrAgreementContext.Provider>
  );
};

export { useSignDrAgreement as default, SignDrAgreementProvider };
